import { useEffect, useState } from "react";
import { api, type Conference, type ReviewerPoolResult } from "../../api.js";
import { apiErrorMessage } from "../../../api/client.js";
import { Alert, Skeleton } from "../../../design/components/Feedback.js";

const deliveryLabels: Record<string, string> = {
  ACCEPTED: "Delivered",
  QUEUED: "Queued",
  RECONCILIATION: "Checking delivery",
  FAILED: "Delivery failed",
  SUPPRESSED: "Suppressed",
  NOT_SENT: "Not sent",
};

const responseLabels: Record<string, string> = {
  PENDING: "Awaiting response",
  ACCEPTED_PENDING_ACCOUNT: "Accepted · setup needed",
  ACCEPTED: "Accepted",
  DECLINED: "Declined",
  EXPIRED: "Expired",
  REVOKED: "Revoked",
};

export function ReviewerPool({ conference, refreshKey }: { conference: Conference; refreshKey: number }) {
  const [pool, setPool] = useState<ReviewerPoolResult | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    setError("");
    void api.reviewerPool(conference.id, conference.organization_id).then((result) => {
      if (active) setPool(result);
    }).catch((reason) => {
      if (active) setError(apiErrorMessage(reason, "Reviewer pool could not be loaded."));
    });
    return () => { active = false; };
  }, [conference.id, conference.organization_id, refreshKey]);

  if (error) return <Alert tone="danger">{error}</Alert>;
  if (!pool) return <div className="reviewer-pool-loading" aria-label="Loading reviewer pool"><Skeleton /><Skeleton /><Skeleton /></div>;

  return <>
    <section className="reviewer-pool-summary" aria-label="Reviewer recruitment summary">
      <div><strong>{pool.summary.invited}</strong><span>Invited</span></div>
      <div><strong>{pool.summary.accepted}</strong><span>Accepted</span></div>
      <div><strong>{pool.summary.active_assignments}</strong><span>Active assignments</span></div>
      <div><strong>{pool.summary.completed_reviews}</strong><span>Completed</span></div>
    </section>
    {pool.rows.length ? <div className="reviewer-pool-table-wrap"><table className="reviewer-pool-table">
      <thead><tr><th>Reviewer</th><th>Invitation</th><th>Workload</th><th>Scope</th></tr></thead>
      <tbody>{pool.rows.map((row) => <tr key={row.id}>
        <td data-label="Reviewer"><strong>{row.name || row.email}</strong>{row.name ? <span>{row.email}</span> : null}</td>
        <td data-label="Invitation"><span>{deliveryLabels[row.delivery_state] ?? row.delivery_state}</span><strong>{responseLabels[row.response_state] ?? row.response_state}</strong></td>
        <td data-label="Workload"><strong>{row.active_assignments} active · {row.completed_reviews} complete</strong><span>Capacity {row.preferred_capacity}</span></td>
        <td data-label="Scope">{row.track?.name ?? "All tracks"}</td>
      </tr>)}</tbody>
    </table></div> : <section className="reviewer-pool-empty"><h2>No reviewers yet</h2><p>Add reviewer email addresses to begin recruitment. Nobody receives manuscript access until they are separately assigned.</p></section>}
  </>;
}
